// src/taskpane/features/integrations/components/IntegrationsPanel.tsx

import React, { useState } from 'react';
import { Plug, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { IntegrationType } from '../types/integrationTypes';
import DartsIPPanel from './dartsip/DartsIPPanel';
import ApigesPanel from './apiges/ApigesPanel';
import EuipoPanel from './euipo/EuipoPanel';
import { cn } from '@/lib/utils';
import { ICON_SIZES } from '@/lib/icon-sizes';

interface IntegrationsPanelProps {
  executionId: string;
  workpillId: string;
}

const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({
  executionId,
  workpillId
}) => {
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType | null>(null);

  const integrations = [
    // {
    //   id: 'dartsip' as IntegrationType,
    //   name: 'Darts IP',
    //   description: 'Intellectual property jurisprudence search',
    //   logo: 'assets/logos/clarivate-logo.png',
    //   // Removed colors to use neutral style
    // },
    // {
    //   id: 'apiges' as IntegrationType,
    //   name: 'APIGES',
    //   description: 'APIGES integration',
    //   logo: 'assets/logos/apiges-logo.png',
    //   // Removed colors to use neutral style
    // },
    {
      id: 'euipo' as IntegrationType,
      name: 'EUIPO',
      description: 'European Union Intellectual Property Office',
      logo: 'assets/logos/euipo-logo.jpg',
      // Removed colors to use neutral style
    }
  ];

  if (selectedIntegration === 'dartsip') {
    return (
      <DartsIPPanel 
        executionId={executionId}
        workpillId={workpillId}
        onBack={() => setSelectedIntegration(null)}
      />
    );
  }

  if (selectedIntegration === 'apiges') {
    return (
      <ApigesPanel 
        onBack={() => setSelectedIntegration(null)}
      />
    );
  }

  if (selectedIntegration === 'euipo') {
    return (
      <EuipoPanel 
        onBack={() => setSelectedIntegration(null)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 mb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Integrations</h2>
            <p className="text-sm text-gray-500 mt-1">
              Connect with external services to enrich your knowledge base
            </p>
          </div>
          
          {/* Mock Button - Explore integrations */}
          <button
            disabled
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white opacity-60 cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <SearchIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Explore integrations</span>
            <FilterIcon className="h-4 w-4 text-gray-400 ml-1" />
          </button>
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 pb-6 min-h-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {integrations.map((integration) => {
            return (
              <div
                key={integration.id}
                onClick={() => setSelectedIntegration(integration.id)}
                className="bg-white border border-gray-100 rounded-xl p-5 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer shadow-sm group min-h-[200px] flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg border border-gray-100 flex items-center justify-center bg-gray-50 text-gray-500 group-hover:text-gray-700 transition-colors">
                    <Plug size={24} />
                  </div>
                  <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100">
                    Available
                  </Badge>
                </div>

                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {integration.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                    {integration.description}
                  </p>
                  
                  <div className="mt-auto pt-2">
                    <img 
                      src={integration.logo} 
                      alt={`${integration.name} logo`}
                      className="h-8 object-contain opacity-80 group-hover:opacity-100 transition-opacity grayscale group-hover:grayscale-0"
                    />
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Add Integration Card */}
          <div className="bg-white border border-dashed border-gray-200 rounded-xl p-5 flex flex-col items-center justify-center text-center opacity-60 cursor-not-allowed min-h-[200px] hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3 text-gray-400">
              <Plus size={24} />
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-1">
              Add integration
            </h3>
            <p className="text-sm text-gray-500">
              Coming soon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple icons for the header button
const SearchIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const FilterIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

export default IntegrationsPanel;
